import React, { useState, useMemo } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import { formatNumber } from '@/utils/formatNumber';
import { FileText, ChevronUp, ChevronDown, Calendar, CalendarDays } from 'lucide-react';
import { Switch } from 'antd';

export interface DailyReportData {
  date: string;
  outdoorTemp: number;
  baselineAC: number;
  actualAC: number;
  savingKwh: number;
  savingPercent: number;
  solar: number;
  grid: number;
  solarPercentage: number;
}

interface MonthlyReportData {
  month: string;
  days: number;
  baseline: number;
  actual: number;
  saving: number;
  savingPercent: number;
  grid: number;
  solar: number;
  solarPercent: number;
}

interface SavingTableProps {
  data: DailyReportData[];
}

const dailyColumnHelper = createColumnHelper<DailyReportData>();
const monthlyColumnHelper = createColumnHelper<MonthlyReportData>();

const dailyColumns = [
  dailyColumnHelper.accessor('date', {
    header: 'Date',
    cell: info => info.getValue(),
    size: 80,
  }),
  dailyColumnHelper.accessor('outdoorTemp', {
    header: 'Out Temp',
    cell: info => formatNumber(info.getValue(), 1),
    size: 70,
  }),
  dailyColumnHelper.accessor('baselineAC', {
    header: 'Baseline',
    cell: info => formatNumber(info.getValue(), 0),
    size: 70,
  }),
  dailyColumnHelper.accessor('actualAC', {
    header: 'Actual',
    cell: info => formatNumber(info.getValue(), 0),
    size: 70,
  }),
  dailyColumnHelper.accessor('savingKwh', {
    header: 'Save kWh',
    cell: info => formatNumber(info.getValue(), 0),
    size: 70,
  }),
  dailyColumnHelper.accessor('savingPercent', {
    header: 'Save %',
    cell: info => {
      const value = info.getValue();
      return (
        <span className={value > 0 ? 'text-primary' : ''}>
          {formatNumber(value, 1)}%
        </span>
      );
    },
    size: 60,
  }),
  dailyColumnHelper.accessor('solar', {
    header: 'Solar',
    cell: info => formatNumber(info.getValue(), 0),
    size: 70,
  }),
  dailyColumnHelper.accessor('grid', {
    header: 'Grid',
    cell: info => formatNumber(info.getValue(), 0),
    size: 70,
  }),
  dailyColumnHelper.accessor('solarPercentage', {
    header: 'Solar %',
    cell: info => {
      const value = info.getValue();
      return (
        <span className={value > 0 ? 'text-green-600' : ''}>
          {formatNumber(value, 1)}%
        </span>
      );
    },
    size: 60,
  }),
];

const monthlyColumns = [
  monthlyColumnHelper.accessor('month', {
    header: 'Month',
    cell: info => info.getValue(),
    size: 100,
  }),
  monthlyColumnHelper.accessor('days', {
    header: 'Days',
    cell: info => info.getValue(),
    size: 60,
  }),
  monthlyColumnHelper.accessor('baseline', {
    header: 'Baseline',
    cell: info => formatNumber(info.getValue(), 0),
    size: 80,
  }),
  monthlyColumnHelper.accessor('actual', {
    header: 'Actual',
    cell: info => formatNumber(info.getValue(), 0),
    size: 80,
  }),
  monthlyColumnHelper.accessor('saving', {
    header: 'Saving',
    cell: info => formatNumber(info.getValue(), 0),
    size: 80,
  }),
  monthlyColumnHelper.accessor('savingPercent', {
    header: 'Saving %',
    cell: info => {
      const value = info.getValue();
      return (
        <span className={value > 0 ? 'text-primary' : ''}>
          {formatNumber(value, 1)}%
        </span>
      );
    },
    size: 80,
  }),
  monthlyColumnHelper.accessor('grid', {
    header: 'Grid',
    cell: info => formatNumber(info.getValue(), 0),
    size: 80,
  }),
  monthlyColumnHelper.accessor('solar', {
    header: 'Solar',
    cell: info => formatNumber(info.getValue(), 0),
    size: 80,
  }),
  monthlyColumnHelper.accessor('solarPercent', {
    header: 'Solar %',
    cell: info => {
      const value = info.getValue();
      return (
        <span className={value > 0 ? 'text-green-600' : ''}>
          {formatNumber(value, 1)}%
        </span>
      );
    },
    size: 80,
  }),
];

export const SavingTable: React.FC<SavingTableProps> = ({ data }) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [isMonthlyView, setIsMonthlyView] = useState(false);

  // Calculate monthly data from daily data
  const monthlyData = useMemo(() => {
    const monthMap = new Map<string, MonthlyReportData>();
    
    data.forEach(row => {
      const date = new Date(row.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, {
          month: monthName,
          days: 0,
          baseline: 0,
          actual: 0,
          saving: 0,
          savingPercent: 0,
          grid: 0,
          solar: 0,
          solarPercent: 0
        });
      }
      
      const monthData = monthMap.get(monthKey)!;
      monthData.days += 1;
      monthData.baseline += row.baselineAC;
      monthData.actual += row.actualAC;
      monthData.saving += row.savingKwh;
      monthData.grid += row.grid;
      monthData.solar += row.solar;
    });
    
    // Calculate percentages
    monthMap.forEach(monthData => {
      monthData.savingPercent = monthData.baseline > 0 ? (monthData.saving / monthData.baseline) * 100 : 0;
      monthData.solarPercent = (monthData.solar + monthData.grid) > 0 ? (monthData.solar / (monthData.solar + monthData.grid)) * 100 : 0;
    });
    
    return Array.from(monthMap.values()).sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateB.getTime() - dateA.getTime();
    });
  }, [data]);

  // Calculate summary data
  const summaryData = React.useMemo(() => {
    if (!data.length) return null;
    
    const totalDays = data.length;
    const totalOutdoorTemp = data.reduce((sum, row) => sum + row.outdoorTemp, 0);
    const totalBaseline = data.reduce((sum, row) => sum + row.baselineAC, 0);
    const totalActual = data.reduce((sum, row) => sum + row.actualAC, 0);
    const totalSaving = data.reduce((sum, row) => sum + row.savingKwh, 0);
    const totalSolar = data.reduce((sum, row) => sum + row.solar, 0);
    const totalGrid = data.reduce((sum, row) => sum + row.grid, 0);
    
    return {
      avgOutdoorTemp: totalOutdoorTemp / totalDays,
      totalBaseline,
      totalActual,
      totalSaving,
      avgSavingPercent: totalBaseline > 0 ? (totalSaving / totalBaseline) * 100 : 0,
      totalSolar,
      totalGrid,
      avgSolarPercent: (totalSolar + totalGrid) > 0 ? (totalSolar / (totalSolar + totalGrid)) * 100 : 0
    };
  }, [data]);
  
  // Calculate monthly summary
  const monthlySummaryData = React.useMemo(() => {
    if (!monthlyData.length) return null;
    
    const totalDays = monthlyData.reduce((sum, row) => sum + row.days, 0);
    const totalBaseline = monthlyData.reduce((sum, row) => sum + row.baseline, 0);
    const totalActual = monthlyData.reduce((sum, row) => sum + row.actual, 0);
    const totalSaving = monthlyData.reduce((sum, row) => sum + row.saving, 0);
    const totalSolar = monthlyData.reduce((sum, row) => sum + row.solar, 0);
    const totalGrid = monthlyData.reduce((sum, row) => sum + row.grid, 0);
    
    return {
      totalDays,
      totalBaseline,
      totalActual,
      totalSaving,
      avgSavingPercent: totalBaseline > 0 ? (totalSaving / totalBaseline) * 100 : 0,
      totalSolar,
      totalGrid,
      avgSolarPercent: (totalSolar + totalGrid) > 0 ? (totalSolar / (totalSolar + totalGrid)) * 100 : 0
    };
  }, [monthlyData]);

  const dailyTable = useReactTable({
    data,
    columns: dailyColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });
  
  const monthlyTable = useReactTable({
    data: monthlyData,
    columns: monthlyColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });
  
  const activeTable = isMonthlyView ? monthlyTable : dailyTable;
  const activeSummaryData = isMonthlyView ? monthlySummaryData : summaryData;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-light-grey h-full flex flex-col">
      <div className="px-4 pt-4 pb-3 border-b border-light-grey">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <div className="h-[18px] w-[18px] bg-light-grey rounded flex items-center justify-center">
              <FileText className="h-3 w-3 text-primary" />
            </div>
            <h3 className="text-dark-blue heading-7">{isMonthlyView ? 'Monthly' : 'Daily'} Energy Report</h3>
          </div>
          <label className="flex items-center gap-2">
            <CalendarDays className="h-3.5 w-3.5 text-dark-grey" />
            <span className="paragraph-12 text-dark-grey">Daily</span>
            <Switch
              checked={isMonthlyView}
              onChange={setIsMonthlyView}
              size="small"
            />
            <span className="paragraph-12 text-dark-grey">Monthly</span>
          </label>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        <table className="w-full">
          <thead className="bg-bg border-b border-light-grey">
            {activeTable.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-2 py-1.5 text-left paragraph-9 font-medium text-dark-grey uppercase tracking-wider cursor-pointer hover:bg-light-grey/50 transition-colors"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-1">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getIsSorted() === 'asc' && (
                        <ChevronUp className="h-2.5 w-2.5 text-primary" />
                      )}
                      {header.column.getIsSorted() === 'desc' && (
                        <ChevronDown className="h-2.5 w-2.5 text-primary" />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-light-grey">
            {activeTable.getRowModel().rows.map(row => (
              <tr key={row.id} className="hover:bg-bg transition-colors">
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className="px-2 py-1 paragraph-10 text-black">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {activeSummaryData && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr className="font-semibold">
                <td className="px-2 py-2 paragraph-9 text-dark-blue">TOTAL</td>
                {isMonthlyView ? (
                  <>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{activeSummaryData.totalDays}</td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(activeSummaryData.totalBaseline, 0)}</td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(activeSummaryData.totalActual, 0)}</td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(activeSummaryData.totalSaving, 0)}</td>
                    <td className={`px-2 py-2 paragraph-9 ${activeSummaryData.avgSavingPercent > 0 ? 'text-primary' : 'text-dark-blue'}`}>
                      {formatNumber(activeSummaryData.avgSavingPercent, 1)}%
                    </td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(activeSummaryData.totalGrid, 0)}</td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(activeSummaryData.totalSolar, 0)}</td>
                    <td className={`px-2 py-2 paragraph-9 ${activeSummaryData.avgSolarPercent > 0 ? 'text-green-600' : 'text-dark-blue'}`}>
                      {formatNumber(activeSummaryData.avgSolarPercent, 1)}%
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-2 paragraph-9 text-center text-dark-blue">{formatNumber(summaryData.avgOutdoorTemp, 1)}</td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(summaryData.totalBaseline, 0)}</td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(summaryData.totalActual, 0)}</td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(summaryData.totalSaving, 0)}</td>
                    <td className={`px-2 py-2 paragraph-9 ${summaryData.avgSavingPercent > 0 ? 'text-primary' : 'text-dark-blue'}`}>
                      {formatNumber(summaryData.avgSavingPercent, 1)}%
                    </td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(summaryData.totalSolar, 0)}</td>
                    <td className="px-2 py-2 paragraph-9 text-dark-blue">{formatNumber(summaryData.totalGrid, 0)}</td>
                    <td className={`px-2 py-2 paragraph-9 ${summaryData.avgSolarPercent > 0 ? 'text-green-600' : 'text-dark-blue'}`}>
                      {formatNumber(summaryData.avgSolarPercent, 1)}%
                    </td>
                  </>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};